// DonateThankyouModal.js

import React, { useEffect, useState, useRef } from "react";

import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

import { getDonationStatus } from "../../apis/user-apis/DonationApis";

const POLL_INTERVAL = 3000; // 3 seconds
const MAX_RETRIES = 10; // 30 seconds total

const DonateThankyouModal = ({
  open,
  handleClose,
  amount = 100,
  donorName = "Friend",
  sessionId = null,
}) => {
  const [paymentStatus, setPaymentStatus] = useState("loading");
  const [serverName, setServerName] = useState("");
  const [serverAmount, setServerAmount] = useState(null);
  const retriesRef = useRef(0);
  const intervalRef = useRef(null);

  const displayName = (serverName || donorName || "").trim() || "Friend";
  const displayAmount = serverAmount || amount;

  useEffect(() => {
    if (!open || !sessionId) {
      // No session_id — fall back to pending (pre-existing behavior)
      if (open && !sessionId) setPaymentStatus("pending");
      return;
    }

    retriesRef.current = 0;
    setPaymentStatus("loading");

    const checkStatus = async () => {
      try {
        const result = await getDonationStatus(sessionId);
        if (result.success) {
          setPaymentStatus(result.paymentStatus);
          if (result.firstName) {
            setServerName(`${result.firstName} ${result.lastName || ""}`.trim());
          }
          if (result.amount) {
            setServerAmount(result.amount);
          }

          // Stop polling if we got a final status
          if (result.paymentStatus === "completed" || result.paymentStatus === "failed" || result.paymentStatus === "refunded") {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return;
          }
        }
      } catch (error) {
        console.error("Error checking donation status:", error);
      }

      retriesRef.current += 1;
      if (retriesRef.current >= MAX_RETRIES) {
        // Stop polling after max retries — show pending with email note
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setPaymentStatus("pending");
      }
    };

    // Initial check
    checkStatus();

    // Start polling
    intervalRef.current = setInterval(checkStatus, POLL_INTERVAL);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [open, sessionId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPaymentStatus("loading");
      setServerName("");
      setServerAmount(null);
      retriesRef.current = 0;
    }
  }, [open]);

  const renderContent = () => {
    switch (paymentStatus) {
      case "loading":
        return (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#F0F0F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 30px",
              }}
            >
              <CircularProgress size={28} sx={{ color: "#081B44" }} />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "24px", md: "36px" },
                fontWeight: 700,
                color: "#081B44",
                fontFamily: "'Playfair Display', serif",
                mb: 2,
              }}
            >
              Processing your payment...
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#6B7280",
                lineHeight: 1.8,
                maxWidth: "700px",
                margin: "0 auto",
                mb: 4,
              }}
            >
              Please wait while we confirm your donation. This usually takes just
              a few seconds.
            </Typography>
          </>
        );

      case "completed":
        return (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#E8F5E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 30px",
              }}
            >
              <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 28 }} />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "30px", md: "44px" },
                fontWeight: 700,
                color: "#081B44",
                fontFamily: "'Playfair Display', serif",
                mb: 2,
              }}
            >
              Thank you, {displayName}!
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#6B7280",
                lineHeight: 1.8,
                maxWidth: "700px",
                margin: "0 auto",
                mb: 4,
              }}
            >
              Your generous{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                ${displayAmount}
              </Box>{" "}
              contribution has been successfully processed. Your support makes a
              real difference in reaching voters across District 8.
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#6B7280",
                lineHeight: 1.8,
                maxWidth: "700px",
                margin: "0 auto",
                mb: 5,
              }}
            >
              A confirmation email has been sent to your inbox. Please keep
              visiting us for campaign updates and share this with your
              neighbors.
            </Typography>
          </>
        );

      case "failed":
      case "refunded":
        return (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#FFEBEE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 30px",
              }}
            >
              <ErrorOutlineIcon sx={{ color: "#C62828", fontSize: 28 }} />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "30px", md: "44px" },
                fontWeight: 700,
                color: "#081B44",
                fontFamily: "'Playfair Display', serif",
                mb: 2,
              }}
            >
              Payment {paymentStatus === "refunded" ? "Refunded" : "Unsuccessful"}
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#6B7280",
                lineHeight: 1.8,
                maxWidth: "700px",
                margin: "0 auto",
                mb: 4,
              }}
            >
              {paymentStatus === "refunded"
                ? "Your donation has been refunded. If you believe this is an error, please contact us."
                : "We were unable to process your payment. Please try again or use a different payment method."}
            </Typography>
          </>
        );

      case "pending":
      default:
        return (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#FFF3E0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 30px",
              }}
            >
              <HourglassEmptyIcon sx={{ color: "#E65100", fontSize: 28 }} />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "30px", md: "44px" },
                fontWeight: 700,
                color: "#081B44",
                fontFamily: "'Playfair Display', serif",
                mb: 2,
              }}
            >
              Thanks for your interest, {displayName}!
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#6B7280",
                lineHeight: 1.8,
                maxWidth: "700px",
                margin: "0 auto",
                mb: 4,
              }}
            >
              Your{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                ${displayAmount}
              </Box>{" "}
              donation is being processed. This may take a moment.
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#6B7280",
                lineHeight: 1.8,
                maxWidth: "700px",
                margin: "0 auto",
                mb: 5,
              }}
            >
              You'll receive a confirmation email once your payment is complete.
              Please keep visiting us for campaign updates and share this with
              your neighbors.
            </Typography>
          </>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: "#081B44",
          px: 4,
          py: 4,
          position: "relative",
        }}
      >
        {/* CLOSE BUTTON */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 18,
            right: 18,
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.4)",
            width: 40,
            height: 40,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#D4A017",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "2px",
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          Contribution
        </Typography>

        <Typography
          sx={{
            color: "#fff",
            fontSize: {
              xs: "28px",
              md: "42px",
            },
            fontWeight: 700,
            lineHeight: 1.2,
            fontFamily: "'Playfair Display', serif",
          }}
        >
          Donate ${displayAmount} to the Campaign
        </Typography>
      </Box>

      {/* CONTENT */}
      <DialogContent
        sx={{
          px: 4,
          py: 8,
          textAlign: "center",
        }}
      >
        {renderContent()}

        {/* BUTTON */}
        <Button
          variant="contained"
          onClick={handleClose}
          sx={{
            bgcolor: "#C99723",
            color: "#081B44",
            fontWeight: 700,
            fontSize: "18px",
            textTransform: "uppercase",
            borderRadius: "4px",
            px: 8,
            py: 1.6,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#B8860B",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>

        {/* FOOTER */}
        <Typography
          sx={{
            fontSize: "13px",
            color: "#6B7280",
            mt: 5,
          }}
        >
          Subject to Delaware campaign finance laws. Individual limit: $600
          /cycle. Not tax deductible.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default DonateThankyouModal;
